import React, { useContext } from 'react';
import AppContext from '../../AppContext';
import styles from '../RowBuilder/Row.module.css';
import HeaderHelmet, { HeaderHelmetProps } from './HeaderHelmet';
import { SubSectionHeaderType } from '../Section/SubSectionHeaderType';
import { INDUSTRY_MEAN_LABEL } from '../model/ColumnLabels';

interface StandardRowHeaderProps {
  readonly helmet: HeaderHelmetProps;
  readonly showPHSHeader?: boolean;
  readonly showPriorYearPHSHeader?: boolean;
  readonly showNAAHeader?: boolean;
  readonly showCASHeader?: boolean;
  readonly showPriorYearCASHeader?: boolean;
  readonly showPercentPointChange?: boolean;
  readonly showDollarChange?: boolean;
  readonly showTDPHeader?: boolean;
  readonly showCalculatePercentTotalCurrYearHeader?: boolean;
  readonly showIndustryMeanHeader?: boolean;
}

export default function StandardRowHeader({
  helmet,
  showPHSHeader,
  showPriorYearPHSHeader,
  showNAAHeader,
  showCASHeader,
  showPriorYearCASHeader,
  showPercentPointChange,
  showDollarChange,
  showTDPHeader,
  showCalculatePercentTotalCurrYearHeader,
  showIndustryMeanHeader
}: StandardRowHeaderProps) {
  const { showBenchmark, showIndustryMean, year, activeYearsMode } = useContext(AppContext);
  const isTenYearMode = activeYearsMode === 'TEN';

  function hasSurplusHeaders() {
    return (
      showPHSHeader ||
      showPriorYearPHSHeader ||
      showCASHeader ||
      showPriorYearCASHeader ||
      showTDPHeader ||
      showCalculatePercentTotalCurrYearHeader
    );
  }

  function getSurplusHeaderText() {
    if (showPHSHeader) {
      return '% of PHS';
    }
    if (showPriorYearPHSHeader) {
      return '% of PY PHS';
    }
    if (showCASHeader) {
      return '% of C&S';
    }
    if (showPriorYearCASHeader) {
      return '% of PY C&S';
    }
    if (showTDPHeader) {
      return '% of Total Direct Premium';
    }
    if (showCalculatePercentTotalCurrYearHeader) {
      return `${year} % of Total`;
    }
    return '';
  }

  function showHeaders() {
    const yearCount = isTenYearMode ? 10 : 5;
    if (helmet.subSectionHeaderType === SubSectionHeaderType.CURRENT_YEAR_ONLY) {
      return (
        <>
          <h5 className="column_align_right">{showIndustryMeanHeader ? INDUSTRY_MEAN_LABEL : ''}</h5>
          <h5 className="column_align_right">{year}</h5>
          {Array.from({ length: yearCount - 1 }).map((_, i) => (
            <h5 key={`blank-${i}`} aria-hidden="true"></h5>
          ))}
        </>
      );
    }
    return (
      <>
        <h5 className="column_align_right">{showIndustryMeanHeader ? INDUSTRY_MEAN_LABEL : ''}</h5>
        {Array.from({ length: yearCount }).map((_, i) => (
          <h5 key={`year-${i}`} className="column_align_right">
            {year - i}
          </h5>
        ))}
      </>
    );
  }

  const yearColsStyle = { '--year-cols': isTenYearMode ? 10 : 5 } as React.CSSProperties;
  return (
    <div
      className={`${styles.container} ${showBenchmark ? '' : styles.collapse} ${!showIndustryMean ? styles.hideIndustryMean : ''}`}
      style={yearColsStyle}
      data-testid="standard-row-header">
      <HeaderHelmet {...helmet} />
      <h5 className="column_align_right" aria-hidden={!showPercentPointChange}>
        {showPercentPointChange ? '%/Pt. Change' : ''}
      </h5>
      <h5 className="column_align_right" aria-hidden={!showDollarChange}>
        {showDollarChange ? '$ Change' : ''}
      </h5>
      <h5 style={{}} className="column_align_right" aria-hidden={!hasSurplusHeaders()}>
        {getSurplusHeaderText()}
      </h5>
      <h5 className="column_align_right" aria-hidden={!showNAAHeader}>
        {showNAAHeader ? '% of NAA' : ''}
      </h5>

      {showHeaders()}
    </div>
  );
}
